import React from "react";
import styled from "styled-components";

const Root = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 0;
    vertical-align: middle;

    svg {
        display: block;
        width: 1em;
        height: 1em;
    }
`;

// The Material Symbols font is loaded globally in src/index.js
// (`import "material-symbols"`). Its own .material-symbols-outlined rule sets
// font-size: 24px, so `&&` is needed to keep sizing driven by Root instead.
const Glyph = styled.span`
    && {
        display: block;
        font-size: 1em;
        line-height: 1;
    }
`;

// material-symbols@0.27.2 ships no `auto_awesome` ligature (only
// auto_awesome_mosaic / auto_awesome_motion), so this one icon stays as inline
// path data carried over from the @mui/icons-material AutoAwesome it replaces.
const INLINE_GLYPHS = {
    auto_awesome: (
        <svg viewBox="0 0 24 24" fill="currentColor" focusable="false" aria-hidden="true">
            <path d="m19 9 1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25z" />
        </svg>
    ),
};

// Ligature names this component accepts. Kept as an explicit allow-list so an
// unknown name still renders nothing, rather than leaking its raw text.
const MATERIAL_ICON_NAMES = new Set([
    "add",
    "attach_file",
    "auto_awesome",
    "check",
    "close",
    "close_fullscreen",
    "content_copy",
    "delete",
    "edit",
    "move_up",
    "open_in_new",
    "remove",
    "send",
    "arrow_downward",
    "arrow_upward",
    // Step navigation in the wizards. The chevrons rather than arrow_back/arrow_forward:
    // they read as "previous/next step" rather than "go back", which is what these buttons do.
    "chevron_left",
    "chevron_right",
    "arrow_circle_down",
    "keyboard_arrow_down",
    "cancel",
    "call",
    "mail",
    "cloud_upload",
    "star",
    "more_vert",
    "person_search",
    "sync_problem",
]);

const resolveMaterialIcon = (icon, fontSize) => {
    if (React.isValidElement(icon)) {
        return icon.props?.fontSize ? icon : React.cloneElement(icon, { fontSize });
    }

    if (typeof icon === "function") {
        const IconComponent = icon;
        return <IconComponent fontSize={fontSize} />;
    }

    if (typeof icon === "string") {
        if (INLINE_GLYPHS[icon]) {
            return INLINE_GLYPHS[icon];
        }

        return MATERIAL_ICON_NAMES.has(icon) ? <Glyph className="material-symbols-outlined">{icon}</Glyph> : null;
    }

    return null;
};

const MaterialIcon = ({
    icon,
    className,
    style,
    onClick,
    fontSize = "inherit",
    title,
    role,
    tabIndex,
    "data-testid": dataTestId,
}) => {
    const resolvedIcon = resolveMaterialIcon(icon, fontSize);
    if (!resolvedIcon) {
        return null;
    }

    const rootStyle = fontSize && fontSize !== "inherit" ? { fontSize, ...style } : style;

    return (
        <Root
            className={className}
            style={rootStyle}
            onClick={onClick}
            title={title}
            role={role}
            tabIndex={tabIndex}
            data-testid={dataTestId}
        >
            {resolvedIcon}
        </Root>
    );
};

export default MaterialIcon;
